/**
 * "(GMT+05:30) Mumbai" — the mobile app's timezone display format (Figma: account profile card).
 *
 * A value already stored in that display form (picked in mobile's timezone list) passes through
 * unchanged; an IANA zone id (e.g. "Asia/Kolkata") or bare "UTC" is formatted from its current
 * GMT offset plus its city segment ("Asia/Kolkata" → "Kolkata", underscores → spaces). Anything
 * unrecognizable is shown verbatim rather than hidden.
 */
export function formatTimeZone(timeZone: string): string {
  const trimmed = timeZone.trim();
  if (!trimmed || trimmed.startsWith('(')) return trimmed;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const zoneName = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    if (!zoneName.startsWith('GMT')) return trimmed;
    // UTC/GMT itself formats as a bare "GMT" — normalize to an explicit zero offset.
    const offset = zoneName === 'GMT' ? 'GMT+00:00' : zoneName;
    const city = (trimmed.split('/').pop() ?? trimmed).replace(/_/g, ' ');
    return `(${offset}) ${city}`;
  } catch {
    // Not a zone Intl knows — show the stored value as-is.
    return trimmed;
  }
}
